import { supabase } from '@/lib/supabase';

/**
 * Client helper for the `auth-guard` edge function.
 *
 * Every authentication action (login, register, password reset) must clear the
 * guard BEFORE the Supabase auth call runs. The guard:
 *   * verifies the Cloudflare Turnstile token server-side with
 *     TURNSTILE_SECRET_KEY (the widget alone is never treated as proof)
 *   * enforces per-IP and per-email throttling with mkt_rate_hit
 *     (5 failures / 15 min => temporary lockout)
 *   * writes every failure to mkt_security_events with a hashed IP
 *
 * Failing OPEN is deliberate for transport errors only: if the function itself
 * is unreachable we do not lock legitimate users out of the product. An
 * explicit `rate_limited` / `captcha_failed` verdict always blocks.
 */
export type AuthKind = 'login' | 'register' | 'reset';

export interface GuardVerdict {
  ok: boolean;
  /** 'rate_limited' | 'captcha_failed' | '' */
  reason: string;
  /** Seconds until the lockout expires. */
  retryAfter: number;
  /** For captcha_failed: 'missing_token' | 'expired_or_reused' | 'invalid'. */
  captchaReason?: string;
}

const PASS: GuardVerdict = { ok: true, reason: '', retryAfter: 0 };

/**
 * NOTE: the Turnstile token is consumed by this call whether it passes or
 * fails. Callers MUST reset the widget afterwards (Turnstile `resetKey`), or a
 * retry re-sends the dead token and fails with "Security check failed" again.
 */
export const guardAuth = async (
  kind: AuthKind,
  email: string,
  token: string,
): Promise<GuardVerdict> => {
  try {
    const { data, error } = await supabase.functions.invoke('auth-guard', {
      body: { action: 'check', kind, email, token },
    });
    // Transport error with no verdict body => fail open (see note above).

    if (error && !data) return PASS;
    if (data?.ok) return PASS;
    return {
      ok: false,
      reason: String(data?.error || 'rate_limited'),
      retryAfter: Number(data?.retryAfter || 0),
      captchaReason: data?.captcha_reason ? String(data.captcha_reason) : undefined,
    };
  } catch {
    return PASS;
  }
};

/** Burn a strike after a rejected credential / failed auth call. */
export const reportAuthFailure = async (kind: AuthKind, email: string) => {
  try {
    await supabase.functions.invoke('auth-guard', { body: { action: 'fail', kind, email } });
  } catch {
    /* non-blocking */
  }
};

/** Clear the counters after a genuine success. */
export const reportAuthSuccess = async (kind: AuthKind, email: string) => {
  try {
    await supabase.functions.invoke('auth-guard', { body: { action: 'ok', kind, email } });
  } catch {
    /* non-blocking */
  }
};

/** Human-readable "try again in X minutes" copy, EN + ES. */
export const guardMessage = (
  verdict: GuardVerdict,
  t: (k: string, vars?: Record<string, string | number>) => string,
  lang: string = 'en',
) => {
  const es = lang === 'es';
  if (verdict.reason === 'captcha_failed') {
    if (verdict.captchaReason === 'expired_or_reused') {
      return es
        ? 'La verificación de seguridad expiró. Ya cargamos una nueva — espere la marca verde y presione el botón otra vez.'
        : 'The security check expired. A fresh one is loading — wait for the green check, then press the button again.';
    }
    if (verdict.captchaReason === 'missing_token') {
      return es
        ? 'La verificación de seguridad aún no terminó. Espere la marca verde debajo del formulario y vuelva a intentarlo.'
        : 'The security check has not finished yet. Wait for the green check below the form, then try again.';
    }
    return t('auth.errCaptcha');
  }
  const minutes = Math.max(Math.ceil((verdict.retryAfter || 60) / 60), 1);
  return t('auth.errTooMany', { minutes });
};

/**
 * Pre-submit check: returns a message when the Turnstile widget has not yet
 * produced a token, so the form does not burn a server call (and a rate-limit
 * hit) on a guaranteed failure. Returns '' when the form may proceed.
 *
 * `softGate` (LOGIN ONLY, 2026-09-24 incident): auth-guard only demands a
 * challenge for login after `loginChallengeAfter` (2) failed attempts. When the
 * widget itself is broken ('error' loop, e.g. Cloudflare 110200 / 600010) or
 * blocked by the browser, a hard client-side block locked EVERY user out of
 * login even though the server would have let them in. With softGate the form
 * proceeds without a token and the server decides — it still returns
 * captcha_failed once the failure threshold is reached.
 */
export const captchaPendingMessage = (
  status: string,
  token: string,
  lang: string = 'en',
  softGate = false,
) => {
  if (token || status === 'disabled') return '';
  if (softGate && (status === 'error' || status === 'blocked' || status === 'expired')) return '';
  const es = lang === 'es';
  if (status === 'blocked') {
    return es
      ? 'La verificación de seguridad está bloqueada en este navegador. Permita challenges.cloudflare.com o use otro navegador.'
      : 'The security check is blocked in this browser. Allow challenges.cloudflare.com or use another browser.';
  }
  if (status === 'error') {
    return es
      ? 'La verificación de seguridad no pudo completarse. Presione "Reintentar ahora" debajo del formulario o recargue la página.'
      : 'The security check could not complete. Press "Retry now" below the form or reload the page.';
  }
  return es
    ? 'La verificación de seguridad aún está cargando. Espere la marca verde y vuelva a intentarlo.'
    : 'The security check is still loading. Wait for the green check, then try again.';
};


